import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";

import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import backgroundImage from "../../assets/images/BG1.jpg";
import logoImage from "../../assets/images/Red-Rhino-Logo.png";
import {
  validatePassword,
  validatePasswordMatch,
} from "../../utils/validationUtils";
import { AuthApiService } from "../../service/authService";
import { authFlowTypes } from "../../constants/authFlowTypes";
import { AxiosError } from "axios";
import { styles } from "../loginScreen/LoginScreen.styles";
import { headingStyles, inputLabelStyles } from "./SetNewPasswordScreen.styles";
import {
  inputContainerStyles,
  textFieldStyles,
} from "../forgotPassword/ForgotPassword.styles";
import useIsMobile from "../../common/mobileVersion/useIsMobile";

const SetNewPasswordScreen: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const flowTypes = searchParams.get("flowType") ?? "";
  const username = searchParams.get("username") ?? "";
  const tempPassword = searchParams.get("resetCode")
    ? (searchParams.get("resetCode") ?? "")
    : (location.href.split("code=")[1] ?? "");
  const IsMobile = useIsMobile();
  const orgId = localStorage.getItem("orgId");

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);
  const handleToggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword((prev) => !prev);

  const handleUpdatePassword = async () => {
    const passwordError = validatePassword(password);
    const matchError = validatePasswordMatch(password, confirmPassword);

    if (matchError) {
      alert(matchError);
      return;
    }

    if (passwordError) {
      alert(passwordError);
      return;
    }
    if (flowTypes === authFlowTypes.newUsers) {
      await authLogin();
    } else if (flowTypes === authFlowTypes.forgotPassword) {
      await authForgotPassword();
    } else alert("Invalid flow type. Please check the URL.");
  };

  const authLogin = async () => {
    try {
      const response = await AuthApiService.login(
        username,
        tempPassword,
        confirmPassword
      );
      if (response.accessToken) {
        // Proceed to the confirmation screen
        navigate(`/${orgId}/confirmation`);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || error.message;
        const errorCode = error.response?.data?.errCode || error.code;
        if (errorCode === "UserNotFoundException") {
          alert(errorMessage || "Login failed. Please try again.");
        } else if (errorCode === "NotAuthorizedException") {
          alert(errorMessage || "Login failed. Please try again.");
        } else if (errorCode == "UserNotConfirmedException") {
          alert(errorMessage || "Login failed. Please try again.");
        } else {
          console.log(error);
          alert("An error occurred. Please try again later.");
        }
      } else {
        // If the error is not an AxiosError
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An unexpected error occurred.";
        console.log(error);
        alert(errorMessage);
      }
    }
  };

  const authForgotPassword = async () => {
    try {
      const response = await AuthApiService.confirmPassword(
        username,
        tempPassword,
        confirmPassword
      );
      if (response) {
        // Proceed to the confirmation screen
        navigate(`/${orgId}/confirmation`);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || error.message;
        const errorCode = error.response?.data?.errCode || error.code;
        if (errorCode === "UserNotFoundException") {
          alert(errorMessage || "Login failed. Please try again.");
        } else if (errorCode === "NotAuthorizedException") {
          alert(errorMessage || "Login failed. Please try again.");
        } else if (errorCode == "UserNotConfirmedException") {
          alert(errorMessage || "Login failed. Please try again.");
        } else {
          console.log(error);
          alert("An error occurred. Please try again later.");
        }
      } else {
        // If the error is not an AxiosError
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An unexpected error occurred.";
        console.log(error);
        alert(errorMessage);
      }
    }
  };

  const togglePasswordVisibility = (showPassword: boolean) =>
    showPassword
      ? (AiOutlineEye({ size: 20 }) as JSX.Element)
      : (AiOutlineEyeInvisible({ size: 20 }) as JSX.Element);

  return (
    <Box sx={styles.container(backgroundImage)}>
      <Box sx={styles.logoContainer(IsMobile)}>
        <Box
          component="img"
          src={logoImage}
          alt="Logo"
          sx={styles.logo(IsMobile)}
        />
      </Box>
      <Box sx={styles.formContainer(IsMobile)}>
        <Typography variant="h2" sx={headingStyles(IsMobile)}>
          New Password
        </Typography>
        {/* Password Input */}
        <Box sx={inputContainerStyles}>
          <Typography variant="body1" sx={inputLabelStyles}>
            Enter new password
          </Typography>
          <TextField
            type={showPassword ? "text" : "password"}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your new password"
            sx={textFieldStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    sx={{ color: "rgba(51, 51, 51, 1)" }}
                  >
                    {togglePasswordVisibility(showPassword)}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Confirm Password Input */}
        <Box sx={inputContainerStyles}>
          <Typography variant="body1" sx={inputLabelStyles}>
            Confirm password
          </Typography>
          <TextField
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter your password"
            sx={textFieldStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleToggleConfirmPasswordVisibility}
                    sx={{ color: "rgba(51, 51, 51, 1)" }}
                  >
                    {togglePasswordVisibility(showConfirmPassword)}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Submit Button */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleUpdatePassword}
          sx={styles.loginButton(IsMobile)}
        >
          UPDATE PASSWORD
        </Button>
      </Box>
    </Box>
  );
};

export default SetNewPasswordScreen;
